import { removeFN } from '@pressreader/utils';

import { Action, Location, State, To } from '../../types';
import { createEntryKey, createHref, createKey, parsePath } from '../../utils';

import { getNextLocation } from './shared';
import { HistoryBackend, Notify } from './types';

const globalHistory = window.history;

export interface HashHistoryOptions {
    /**
     * Namespace inserted after '#', so the SDK's hashes can coexist with a host
     * page's own hash router and `#anchor` links. Default '' → `#/path` (legacy).
     * e.g. 'pr=' → `#pr=/path` (key=value form is robust: it is not a valid CSS
     * anchor target, so host `#section` jumps never collide). Hash changes outside
     * the namespace are ignored.
     */
    prefix?: string;
    /**
     * 'push' (default) grows the host page's Back stack on each navigation;
     * 'replace' never adds a Back entry, so the host's Back button takes the user
     * out of the SDK rather than through its internal history.
     */
    backButton?: 'push' | 'replace';
}

/**
 * Hash backend: navigation lives entirely inside window.location.hash, so the SDK
 * can run embedded under a host page's URL. Owns the hashchange listener, the
 * per-hash state Map (so back/forward restore the right state object + key), and
 * the self-push suppression queue. The optional namespace prefix and back-button
 * policy let the SDK coexist with a host page. All instance state is released on
 * dispose(), which is what makes the SDK lifecycle reversible.
 */
export function createHashBackend(notify: Notify, options: HashHistoryOptions = {}): HistoryBackend {
    const prefix = options.prefix ?? '';
    const backButton = options.backButton ?? 'push';

    // In-memory state storage, keyed by the full (namespaced) hash string.
    const hashStates = new Map<string, { state: State; key: string; entryKey: string }>();

    // Queued target hashes for in-flight self-pushes. Each push that will actually
    // change window.location.hash enqueues the target; the resulting hashchange
    // event matches by event.newURL and removes its own entry instead of being
    // treated as a user-initiated POP. A per-URL queue (rather than a single
    // boolean flag) fixes two races the flag couldn't handle (regression tests in
    // history.hash.spec.ts):
    //   1. Duplicate-hash push fires no event — guarded by the `!== hashUrl` check
    //      in push(), so nothing is enqueued and the queue can't leak into the
    //      next real POP.
    //   2. Back-to-back different-hash pushes queue multiple hashchange events —
    //      each event matches and removes its own entry, instead of all events
    //      racing for the same single flag bit.
    const pendingSelfHashChanges: string[] = [];

    // '#' + namespace prefix (default '#'); the app-root variant ('#/' by default);
    // and ownership / route-extraction helpers built on them.
    const marker = () => '#' + prefix;
    const rootHash = () => marker() + '/';
    const owns = (hash: string) => hash.startsWith(marker());
    const toHashUrl = (nextLocation: Location) => marker() + extractHashUrl(createHref(nextLocation)).slice(1);

    const onHashChange = (e: HashChangeEvent) => {
        const newHash = new URL(e.newURL).hash || rootHash();
        // Ignore fragments outside our namespace (host anchors / host hash router).
        if (!owns(newHash)) {
            return;
        }
        const idx = pendingSelfHashChanges.indexOf(newHash);
        if (idx >= 0) {
            pendingSelfHashChanges.splice(idx, 1);
            return;
        }
        notify('POP');
    };

    window.addEventListener('hashchange', onHashChange);
    hashStates.set(window.location.hash || rootHash(), { state: {}, key: createKey(), entryKey: createEntryKey() });

    return {
        kind: 'hash',

        push(to: To, state: State = {}): void {
            const nextLocation = getNextLocation(to, state);
            const hashUrl = toHashUrl(nextLocation);

            hashStates.set(hashUrl, {
                state: removeFN(nextLocation.state) as State,
                key: nextLocation.key,
                entryKey: nextLocation.entryKey ?? createEntryKey(),
            });

            if (backButton === 'replace') {
                // Replace-only: never grow the host page's Back stack. replaceState
                // fires no hashchange, so no self-push suppression is needed.
                if (window.location.hash !== hashUrl) {
                    globalHistory.replaceState(globalHistory.state, '', hashUrl);
                }
                notify('PUSH');
                return;
            }

            // Setting window.location.hash to the current value fires no event —
            // only enqueue when the assignment will actually produce one, otherwise
            // the entry would sit in the queue and silently consume a later real POP.
            if (window.location.hash !== hashUrl) {
                pendingSelfHashChanges.push(hashUrl);
            }
            window.location.hash = hashUrl;

            notify('PUSH');
        },

        replace(to: To, state: State = {}, action: Action = 'REPLACE'): void {
            // Same logical entry, new content: carry the entry identity from the current hash.
            const nextLocation = getNextLocation(to, state, hashStates.get(window.location.hash || rootHash())?.entryKey);
            const hashUrl = toHashUrl(nextLocation);

            hashStates.set(hashUrl, {
                state: removeFN(nextLocation.state) as State,
                key: nextLocation.key,
                entryKey: nextLocation.entryKey ?? createEntryKey(),
            });

            // Only touch the URL if the hash actually changed.
            // replaceState doesn't fire hashchange, so no suppression needed.
            if (window.location.hash !== hashUrl) {
                globalHistory.replaceState(globalHistory.state, '', hashUrl);
            }

            notify(action);
        },

        getLocation(): Location {
            const hash = window.location.hash || rootHash();
            const routePath = owns(hash) ? hash.slice(marker().length) : '/';
            const parsed = parsePath(routePath || '/');
            const stateEntry = hashStates.get(hash);

            return {
                pathname: parsed.pathname || '/',
                search: parsed.search || '',
                hash,
                href: window.location.href,
                state: stateEntry?.state ?? {},
                key: stateEntry?.key ?? 'default',
                entryKey: stateEntry?.entryKey,
            };
        },

        go(delta: number): void {
            // The native hashchange from this traversal is caught by onHashChange
            // and turned into a POP (or suppressed if it matches a pending push).
            globalHistory.go(delta);
        },

        dispose(): void {
            window.removeEventListener('hashchange', onHashChange);
            hashStates.clear();
            pendingSelfHashChanges.length = 0;
        },
    };
}

function extractHashUrl(url: string): string {
    const hashIndex = url.indexOf('#');
    if (hashIndex >= 0) {
        return url.slice(hashIndex);
    }
    return url.startsWith('/') ? '#' + url : '#/' + url;
}
